import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Target } from "lucide-react";
import { toast } from "react-toastify";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/misc/Card";
import { Button } from "../../components/misc/Button";
import ConfirmPopup from "../../components/misc/ConfirmPopup";
import CustomToast from "../../components/misc/CustomToast";
import { getApiErrorMessage } from "../../utils/apiError";
import { cn } from "../../utils/classMerger";
import { useIsMobile } from "../../hooks/useIsMobile";
import InteractiveAiUsageBar from "../../components/niche/InteractiveAiUsageBar";
import NicheChatThread, { ChatMessage } from "../../components/niche/NicheChatThread";
import NicheComposer, { NichePinnedBar } from "../../components/niche/NicheComposer";
import NicheProfileForm from "../../components/niche/NicheProfileForm";
import NicheRefreshNote from "../../components/niche/NicheRefreshNote";
import NicheUpsell from "../../components/niche/NicheUpsell";
import {
    answerInterview, discardInterview, finishInterview, nicheErrorCode, nicheErrorExtra, saveInterviewProfile, startInterview,
} from "../../components/niche/nicheApi";
import { refillText } from "../../components/niche/nicheErrors";
import { interviewStep, NICHE_TOPICS, turnStep } from "../../components/niche/nicheTopics";
import { InterviewReply, InterviewTurn, NicheArea, NicheOverview, NicheProfile } from "../../components/niche/types";
import { useNiche } from "../../components/niche/useNiche";
import "./MyNicheScreen.css";

const fail = (e: unknown, fallback: string): void => {
    toast.error(<CustomToast message={getApiErrorMessage(e, fallback)} type="error" />);
};

// This session's back-and-forth, by turn. A question keeps the topic and progress from the reply
// that asked it, which lands before the reload; an answer keeps the turn it answered.
interface Exchange { question: string; answer?: string; area: NicheArea; covered?: number; turn?: number }
// Drops a trailing question this session asked but nobody answered here.
const dropUnanswered = (xs: Exchange[]): Exchange[] => (xs.length && !xs[xs.length - 1].answer ? xs.slice(0, -1) : xs);

// The conversation so far: the api's turns with this session's exchanges laid over them by turn.
// The api wins once it has a turn; until its reload lands, an answer it hasn't listed yet and the
// question the reply brought fill in. An older api sends no turns: then it's this session's
// exchanges and the pending question, as before.
const transcript = (iv: NicheOverview["interview"], exchanges: Exchange[]): InterviewTurn[] => {
    let turns: InterviewTurn[];
    if (iv.turns) {
        turns = iv.turns.map((t) => ({ ...t }));
        for (const x of exchanges) {
            if (x.turn === undefined) continue;
            const t = turns[x.turn];
            if (t) t.answer = t.answer || x.answer;
            else if (x.turn === turns.length) turns.push({ area: x.area, question: x.question, answer: x.answer });
        }
    } else {
        turns = exchanges.map(({ area, question, answer }) => ({ area, question, answer }));
        const pq = iv.pendingQuestion;
        if (pq && (!turns.length || turns[turns.length - 1].answer)) turns.push({ area: pq.area, question: pq.text });
    }
    // Only an interview waiting on an answer has an open question (a stale in-session one goes).
    const waiting = iv.status === "in_progress" && !iv.awaitingWriteUp;
    if (!waiting && turns.length && !turns[turns.length - 1].answer) turns.pop();
    return turns;
};

// Below the chat card: the page's bottom padding and, on a phone, the bottom nav (UserLayout's <main> padding).
const belowCard = (mobile: boolean): string => (mobile ? "1rem + 5rem + env(safe-area-inset-bottom)" : "1.5rem");

interface NicheCardProps {
    chat: boolean;
    mobile: boolean;
    children: React.ReactNode;
}

// The page's card. In the interview and its review it's a column the height of the screen less what sits
// around it, and the conversation scrolls inside it. What's above it is measured (the app bar, an admin's
// impersonation banner or maintenance bar, the page padding); until then it's the plain app bar and padding.
const NicheCard: React.FC<NicheCardProps> = ({ chat, mobile, children }) => {
    const card = useRef<HTMLDivElement>(null);
    const [top, setTop] = useState<number | null>(null);
    // Again on a resize or a turn, and when the page padding changes with the breakpoint.
    useLayoutEffect(() => {
        const el = card.current;
        if (!chat || !el) return;
        // From the top of the page, so a page scrolled a little (a window under the floor) doesn't skew it.
        const measure = (): void => setTop(el.getBoundingClientRect().top + window.scrollY);
        measure();
        window.addEventListener("resize", measure);
        window.addEventListener("orientationchange", measure);
        return () => {
            window.removeEventListener("resize", measure);
            window.removeEventListener("orientationchange", measure);
        };
    }, [chat, mobile]);
    const reserved = top === null ? (mobile ? "calc(11rem + env(safe-area-inset-bottom))" : "7rem") : `calc(${top}px + ${belowCard(mobile)})`;
    return (
        <Card
            ref={card}
            // The reserved space is everything around the card, so it drops its own top margin.
            className={cn("w-full max-w-4xl mx-auto", chat ? "niche-chat-card mt-0 flex flex-col" : "h-auto")}
            style={chat ? { ["--niche-chat-reserved" as string]: reserved } : undefined}
        >
            {children}
        </Card>
    );
};

const MyNicheScreen: React.FC = () => {
    const { overview, loading, error, refresh } = useNiche();
    // The same page padding as the other top-level sidebar pages (AnalyticsScreen).
    const mobile = useIsMobile(900);
    const page = (children: React.ReactNode): JSX.Element => <div className={cn("w-full", mobile ? "p-4" : "p-6")}>{children}</div>;
    const [exchanges, setExchanges] = useState<Exchange[]>([]);
    // The overview this session's exchanges were written against.
    const [heldFor, setHeldFor] = useState<NicheOverview | null>(null);
    const [answer, setAnswer] = useState("");
    // The answer on its way: it shows in the thread while the box is empty and locked.
    const [sending, setSending] = useState<string | null>(null);
    // What's in flight: an AI turn (an answer, the write-up retry) shows "Thinking…";
    // anything else (start, save, discard) only locks the buttons.
    const [pending, setPending] = useState<null | "ai" | "other">(null);
    const busy = pending !== null;
    const [notice, setNotice] = useState<string | null>(null);
    // The review form's save errors, listed on the form like Settings → My Niche.
    const [errors, setErrors] = useState<string[]>([]);
    // The one-step-too-easy actions ask first: a redo replaces the saved niche once saved and costs
    // allowance along the way; discarding a draft or stopping a redo throws the answers away.
    const [confirming, setConfirming] = useState<null | "redo" | "discard" | "stopRedo">(null);
    // The paused line describes every button it disables (Tab skips a disabled button).
    const pausedId = useId();
    // A reload that lands unpaused (a reservation settled lower, a billing reset) retires a refusal's
    // refill notice; a failed reload keeps it, since the page it would correct is stale.
    useEffect(() => {
        if (overview && !overview.allowance.paused && !error) setNotice(null);
    }, [overview, error]);
    // An api that lists the turns carries this session's exchanges itself once a newer overview lands,
    // so they're ignored from that very render (never drawn over an interview restarted in another tab),
    // then cleared; a failed reload keeps them, since the page is stale.
    const live = overview?.interview.turns && !error && overview !== heldFor ? [] : exchanges;
    useEffect(() => {
        if (overview?.interview.turns && !error) setExchanges((xs) => (xs.length ? [] : xs));
    }, [overview, error]);
    // "Welcome back" is for an interview that already had answers when the page opened.
    const resumed = useRef<boolean | null>(null);
    if (overview && resumed.current === null) {
        resumed.current = overview.interview.status === "in_progress" && !!overview.interview.turns?.some((t) => t.answer);
    }

    // The reply to an answer is the next pending question, so an unanswered
    // in-session exchange wins; a return visit resumes at the api's question.
    const last = live[live.length - 1];
    const asked = last && !last.answer ? last : undefined;
    const pendingQuestion = overview?.interview.pendingQuestion;
    const current = asked ? asked.question : pendingQuestion?.text;
    const currentArea = asked ? asked.area : pendingQuestion?.area;
    // Sent with the answer, so a tab that fell behind gets a 409 instead of answering the next question.
    const currentTurn = asked ? asked.turn : pendingQuestion?.turn;
    // Typed text belongs to the question it was typed under, so a new question (a new
    // interview, a lost reply the api recorded, a quiet 409) empties the box. A failure
    // that leaves the question in place keeps the text.
    useEffect(() => setAnswer(""), [current]);

    // The box is disabled while a reply is on its way, which drops focus; after the agent's
    // own Start or Send, put the cursor back once the next question is up.
    const answerRef = useRef<HTMLTextAreaElement>(null);
    const refocusAnswer = useRef(false);
    useEffect(() => {
        if (busy || !refocusAnswer.current) return;
        refocusAnswer.current = false;
        if (!error) answerRef.current?.focus();
    }, [busy, error]);

    // The api's answer() fills whichever turn is last on the server, so once a request
    // fails (or the write-up retry replies) the server is the source of truth: drop the
    // in-session question and reload, and `current` falls through to the api's question.
    const resync = async (): Promise<void> => {
        setExchanges(dropUnanswered);
        await refresh();
    };

    const run = async (
        fn: () => Promise<unknown>,
        fallback: string,
        { ai = false, resyncOnFail = false, quietConflict = false } = {},
    ): Promise<void> => {
        setPending(ai ? "ai" : "other");
        setNotice(null);
        setErrors([]);
        try {
            await fn();
        } catch (e) {
            const code = nicheErrorCode(e);
            const conflict = code === "NICHE_INTERVIEW_STATE";
            if (code === "AI_ALLOWANCE_EXHAUSTED") {
                // The interview is charged like every other AI call; the meter and the buttons follow after the reload.
                setNotice(refillText(nicheErrorExtra<string>(e, "resetsOn") ?? null));
            } else if (code === "NICHE_PROFILE_INVALID") setErrors(nicheErrorExtra<string[]>(e, "errors") ?? []);
            // Another tab moved the interview on: a Send just follows it, but a Save, Discard
            // or Start the agent chose must say it didn't happen.
            else if (conflict && !quietConflict) {
                toast.error(<CustomToast message="Your niche changed in another tab, so that didn't go through. Here's the latest." type="error" />);
            } else if (!conflict) fail(e, fallback);
            // Lost AI Pro mid-session (plan change, allowance set to 0): reload, so the page shows the upsell.
            // When the allowance runs out, reload too, so the meter and the disabled buttons are the api's.
            if (resyncOnFail || conflict || code === "NICHE_NOT_ELIGIBLE" || code === "AI_ALLOWANCE_EXHAUSTED") await resync();
        } finally {
            setPending(null);
        }
    };

    if (loading) return page(<p className="text-sm text-gray-500">Loading…</p>);
    // Only a first load that failed; a failed refresh keeps the page (NicheRefreshNote below).
    if (!overview) {
        return page(
            <p className="flex items-center gap-2 text-sm">
                Something went wrong.{" "}
                <Button type="button" size="sm" variant="outline" onClick={() => void refresh()}>Try again</Button>
            </p>,
        );
    }

    const iv = overview.interview;
    // After a failed reload the overview is stale: the question shown may already be answered
    // (a Send would file the text against the next one), and a Start may already have opened
    // an interview a second one would restart. Every action waits until the note's Try again
    // reloads the page.
    const locked = busy || error;
    // While the allowance is paused nothing that would spend it can start; the way back, Discard and Save (no AI) stay open.
    const paused = overview.allowance.paused;

    const start = (): Promise<void> =>
        run(async () => {
            refocusAnswer.current = true;
            const reply = await startInterview();
            if (reply.type === "question") {
                setExchanges([{ question: reply.text, area: reply.area, covered: reply.covered, turn: reply.turn }]);
                setHeldFor(overview);
                resumed.current = false;
            }
            await refresh();
        }, "Couldn't start the interview. Try again.");

    const send = (): Promise<void> =>
        run(async () => {
            const text = answer.trim();
            // No question on screen, nothing to answer.
            if (!text || !currentArea) return;
            refocusAnswer.current = true;
            // Like a chat: the box empties and the answer shows in the thread while the reply is on its way.
            setSending(text);
            setAnswer("");
            let reply: InterviewReply;
            try {
                reply = await answerInterview(text, currentTurn);
            } catch (e) {
                // It didn't go: the text goes back in the box (a new question empties it again).
                setAnswer(text);
                throw e;
            } finally {
                setSending(null);
            }
            // Record the question the agent saw (the in-session one, or the api's after a resync).
            setExchanges((xs) => {
                const done = [...dropUnanswered(xs), { question: current ?? "", answer: text, area: currentArea, turn: currentTurn }];
                return reply.type === "question"
                    ? [...done, { question: reply.text, area: reply.area, covered: reply.covered, turn: reply.turn }]
                    : done;
            });
            setHeldFor(overview);
            await refresh();
        }, "Couldn't send that answer. Try again.", { ai: true, resyncOnFail: true, quietConflict: true });

    const discard = (fallback: string): Promise<void> =>
        run(async () => { await discardInterview(); setExchanges([]); await refresh(); }, fallback);

    const confirms = {
        redo: { title: "Redo the interview?", message: "Your current niche stays until you save a new one. The interview uses a little of your AI allowance.", go: start },
        discard: {
            title: "Discard this draft?",
            message: "Your answers from this interview will be lost. Your saved niche, if you have one, is unchanged.",
            go: () => discard("Couldn't discard. Try again."),
        },
        stopRedo: {
            title: "Stop this redo?",
            message: "Your answers so far will be lost. Your current niche is unchanged.",
            go: () => discard("Couldn't stop the redo. Try again."),
        },
    };

    const step = asked
        ? interviewStep(asked.area, asked.covered ?? iv.covered)
        : iv.pendingQuestion && interviewStep(iv.pendingQuestion.area, iv.covered);
    // After a follow-up on the last topic nothing is left to ask, so the write-up is next.
    const writingUp = step?.followUp && step.number === NICHE_TOPICS.length;

    // The thread: every question in a bot bubble led by its topic's line, every answer in the agent's.
    const turns = transcript(iv, live);
    const openAt = turns.length && !turns[turns.length - 1].answer ? turns.length - 1 : -1;
    const messages = turns.flatMap((t, i): ChatMessage[] => {
        // The open question takes the header's step (exact even when an older api sends no turns).
        const lead = (i === openAt ? step : turnStep(turns, i))?.line;
        const said = t.answer || (i === openAt ? sending : null);
        return [{ id: `q${i}`, from: "bot", lead, text: t.question }, ...(said ? [{ id: `a${i}`, from: "me" as const, text: said }] : [])];
    });
    const systemLine = resumed.current ? "Welcome back. Here's where we left off." : "Your answers save as you go. Leave anytime and pick up here.";
    const review = iv.status === "review";
    const covered = review ? iv.total : iv.covered;
    // Above the conversation's scroll region, so it stays in view.
    const progress = (
        <div data-testid="niche-progress" className="flex items-center gap-4 border-b border-gray-200 bg-white px-6 py-2">
            <div className="min-w-0 flex-1 space-y-1.5">
                <p className="text-sm font-medium text-gray-900">
                    {step ? `Topic ${step.number} of ${iv.total} · ${step.name}` : `${covered} of ${iv.total} topics covered`}
                </p>
                <div role="progressbar" aria-label="Interview progress" aria-valuemin={0} aria-valuemax={iv.total} aria-valuenow={covered} className="h-2 rounded bg-gray-200 overflow-hidden">
                    <div className="h-2 bg-primary" style={{ width: `${(covered / Math.max(1, iv.total)) * 100}%` }} />
                </div>
            </div>
            {/* A redo: the saved niche stays until a new one is saved, so offer the way back. */}
            {!review && overview.profile && (
                <Button variant="outline" size="sm" disabled={locked} onClick={() => setConfirming("stopRedo")}>
                    Keep my current niche
                </Button>
            )}
        </div>
    );

    // What explains a locked box or a refused action: the paused line and a refusal's notice, in a live
    // region mounted before either (a live region that mounts filled is skipped), then a failed reload's
    // note. A refusal's notice explains it until the reload lands; once the overview says paused, the
    // paused line carries the same sentence, so the notice steps aside.
    const notes = (pinnedHere: boolean): JSX.Element => (
        <>
            <div role="status" className={pinnedHere ? undefined : "px-6"}>
                {overview.eligible && paused && (
                    <p id={pausedId} className="pb-2 text-sm text-[#d23600]">
                        {/* Only an interview under way picks up again; on the review Save works, so no way round is needed. */}
                        {refillText(overview.allowance.resetsOn)}
                        {iv.status === "in_progress" && " The interview picks up again then."}
                        {iv.status !== "review" && (
                            <>
                                {" "}You can still <Link to="/settings/my-niche" className="underline">edit My Niche in Settings</Link>.
                            </>
                        )}
                    </p>
                )}
                {notice && !paused && <p className={cn("text-sm text-[#d23600]", pinnedHere && "pb-2")}>{notice}</p>}
            </div>
            {error && <NicheRefreshNote className={pinnedHere ? "px-0 pb-2" : undefined} onRetry={() => void refresh()} />}
        </>
    );
    // During the interview they're pinned with the box they explain (above it, or on their own after the
    // write-up failure), so a thread that opens at its newest message never leaves them scrolled away.
    const pinNotes = overview.eligible && iv.status === "in_progress";

    // In the interview and its review the card is a column the height of the screen, and the conversation
    // scrolls inside it (NicheCard).
    let chat = false;
    const chatBody = "flex min-h-0 flex-1 flex-col p-0";
    let body: React.ReactNode;
    if (!overview.eligible) {
        body = <NicheUpsell />;
    } else if (iv.status === "in_progress") {
        chat = true;
        body = (
            <CardContent className={chatBody}>
                {progress}
                <NicheChatThread
                    // The retry asks for whatever didn't come: the next question mid-interview, else the summary.
                    messages={iv.awaitingWriteUp
                        ? [...messages, { id: "write-up-failed", from: "bot", text: iv.covered < iv.total ? "We couldn't get the next question." : "We couldn't finish your summary." }]
                        : messages}
                    // Not once the reply's question is up, though the reload after it keeps the box locked.
                    typing={pending === "ai" && (sending !== null || iv.awaitingWriteUp)}
                    systemLine={systemLine}
                >
                    {iv.awaitingWriteUp && (
                        <div className="pl-9">
                            <Button disabled={locked || paused} aria-describedby={paused ? pausedId : undefined} onClick={() => run(async () => { await finishInterview(); await resync(); }, "Still couldn't finish. Try again in a minute.", { ai: true, resyncOnFail: true })}>
                                Try again
                            </Button>
                        </div>
                    )}
                    {/* Each answer is an AI turn that takes seconds (the typing dots); the write-up after the last one takes longest, so it's said. */}
                    {pending === "ai" && (
                        <p role="status" className={writingUp ? "flex items-center gap-2 text-sm text-gray-500" : "sr-only"}>
                            {writingUp ? <><Loader2 className="h-4 w-4 animate-spin text-primary" /> Putting your niche together…</> : "Thinking…"}
                        </p>
                    )}
                </NicheChatThread>
                {/* Locked while a reply is on its way (or the page is stale), so nothing typed meanwhile is lost to the reset. */}
                {iv.awaitingWriteUp ? (
                    // Flat until it has something to say; its status region is there before that.
                    <NichePinnedBar className={paused || notice || error ? undefined : "border-0 p-0"}>
                        {notes(true)}
                    </NichePinnedBar>
                ) : (
                    <NicheComposer
                        ref={answerRef}
                        value={answer}
                        onChange={setAnswer}
                        onSend={() => void send()}
                        disabled={locked}
                        sendDisabled={paused}
                        describedBy={paused ? pausedId : undefined}
                        note={notes(true)}
                    />
                )}
            </CardContent>
        );
    } else if (review && iv.draftProfile) {
        const summary: ChatMessage = {
            id: "summary",
            from: "bot",
            lead: "That's everything, thank you!",
            text: <>Here's your niche in a sentence: <i>{iv.draftProfile.nicheStatement}</i><br />Take a look below, fix anything that's off, and save it.</>,
        };
        chat = true;
        body = (
            <CardContent className={chatBody}>
                {progress}
                {/* The conversation is finished: nothing left to save as you go. One bounded box holds it and the
                    form; it opens at the summary, so the agent reads the niche first and scrolls down to save it. */}
                <NicheChatThread
                    messages={[...messages, summary]}
                    startAt="summary"
                    footer={
                        <div className="border-t border-gray-200 px-6 py-4">
                            <NicheProfileForm
                                initial={iv.draftProfile}
                                submitLabel="Save my niche"
                                errors={errors}
                                busy={locked}
                                onSubmit={(p: NicheProfile) => run(async () => { await saveInterviewProfile(p); setExchanges([]); await refresh(); }, "Couldn't save your niche. Try again.")}
                                secondary={
                                    <Button type="button" variant="outline" disabled={locked} onClick={() => setConfirming("discard")}>
                                        Discard
                                    </Button>
                                }
                            />
                        </div>
                    }
                />
            </CardContent>
        );
    } else if (!overview.profile) {
        body = (
            <CardContent className="space-y-4 pb-8">
                <p className="text-sm text-gray-600">About 10 minutes. 6 topics, and we may ask a quick follow-up on some.</p>
                <ol aria-label="Interview topics" className="list-decimal space-y-1 pl-5 text-sm text-gray-600">
                    {NICHE_TOPICS.map((t) => <li key={t.area}>{t.name}</li>)}
                </ol>
                <div className="flex gap-2">
                    {/* A first niche and any interview thrown away cost nothing beyond the questions asked. */}
                    <Button disabled={locked || paused} aria-describedby={paused ? pausedId : undefined} onClick={() => void start()}>
                        Start interview
                    </Button>
                    <Link to="/settings/my-niche" className="self-center text-sm underline">I already know my niche</Link>
                </div>
            </CardContent>
        );
    } else {
        const p = overview.profile;
        body = (
            <CardContent className="space-y-4 pb-8">
                <p className="text-lg">{p.nicheStatement}</p>
                <p className="text-sm text-gray-600">{p.audience}</p>
                <p className="text-sm">Based in {p.homeBase.town}, {p.homeBase.state}{p.serviceAreas.length ? ` · serving ${p.serviceAreas.map((a) => a.town).join(", ")}` : ""}</p>
                {p.specialties.length > 0 && <p className="text-sm">Specialties: {p.specialties.join(", ")}</p>}
                {p.differentiators.length > 0 && <p className="text-sm">What makes me different: {p.differentiators.join(", ")}</p>}
                <div className="flex gap-2 flex-wrap">
                    {/* Asks first: the current niche stays, and the interview spends allowance. */}
                    <Button variant="outline" disabled={locked || paused} aria-describedby={paused ? pausedId : undefined} onClick={() => setConfirming("redo")}>Redo the interview</Button>
                    <Link to="/settings/my-niche" className="self-center text-sm underline">Edit in Settings</Link>
                </div>
            </CardContent>
        );
    }

    return page(
        <NicheCard chat={chat} mobile={mobile}>
            <CardHeader>
                <CardTitle className="flex items-center"><Target className="h-6 w-6 mr-2" /> My Niche</CardTitle>
                <CardDescription>Who you're best at helping, and where.</CardDescription>
            </CardHeader>
            {/* Every state draws on the allowance now (each question and the write-up), so the meter is always in view. */}
            {overview.eligible && (
                <div className="px-6 pb-2">
                    <InteractiveAiUsageBar allowance={overview.allowance} />
                </div>
            )}
            {!pinNotes && notes(false)}
            {/* Screen readers skip a live region that mounts already filled, and the question's own
                spot mounts filled on Start and after the write-up retry. This one is here in every
                state, so every question is announced, the first one too. */}
            <p data-testid="question-announcer" aria-live="polite" className="sr-only">
                {overview.eligible && iv.status === "in_progress" && !iv.awaitingWriteUp && current
                    ? `${step ? `Topic ${step.number} of ${iv.total}: ${step.name}. ${step.line} ` : ""}Question: ${current}`
                    : ""}
            </p>
            {body}
            {confirming && (
                <ConfirmPopup
                    open
                    title={confirms[confirming].title}
                    message={confirms[confirming].message}
                    onConfirm={() => {
                        setConfirming(null);
                        // The popup doesn't trap focus, so another action can start behind it; the page
                        // may have moved on by then, so a confirm that lands mid-action does nothing.
                        if (!locked) void confirms[confirming].go();
                    }}
                    onCancel={() => setConfirming(null)}
                />
            )}
        </NicheCard>
    );
};

export default MyNicheScreen;
